import { create } from 'zustand';

export type ListingSide = 'seeking_advertiser' | 'seeking_media' | 'seeking_member' | 'barter';

export interface MarketListingRow {
 listing_hash: string;
 mo_family_hash: string;
 side: ListingSide;
 title: string;
 description?: string | null;
 period_start: string;
 period_end: string;
 price_per_impression: string;  // numeric → string
 currency: string;
 industry_ksic_major?: string | null;
 region_sido?: string | null;
 region_gugun?: string | null;
 is_active: boolean;
 is_expired?: boolean;
 created_at: string;
 family_name?: string | null;
 family_description?: string | null;
 /** 광고주 listing(seeking_media)의 슬롯 점유 영상 썸네일 경로 (최대 4장). 미디어사 listing 은 빈 배열. */
 thumbnails?: string[];
 /** 스크린 보유 매물 카드 미리보기 — 첫 스크린의 첫 설치 사진 / 외부로 내보내는 첫 영상. */
 screen_preview?: { client_hash: string; name: string; photo_url: string } | null;
 outbound_preview?: { file_hash: string; filename: string; thumbnail_path: string } | null;
 /** 미디어사 listing(seeking_advertiser)의 총 스크린 수 (family + 모든 하위 family 활성 mo_clients). 광고주 listing 은 null. */
 screen_count?: number | null;
 /** 모패밀리 대표 이미지 URL. 미설정 + 비기본 모패밀리 → null (클라가 이니셜 placeholder). */
 family_image_url?: string | null;
 /** 모패밀리 owner 모피어 정보 — 카드에서 owner 아바타/이름 노출용. */
 owner_mo_peer_hash?: string | null;
 owner_name?: string | null;
 owner_profile_image?: string | null;
}

export interface ListingFilter {
 side: ListingSide;
 industry?: string;
 sido?: string;
 gugun?: string;
 priceMin?: number;
 priceMax?: number;
 q?: string;
}

interface MarketState {
 filter: ListingFilter;
 page: number;     // 0-indexed
 limit: number;
 rows: MarketListingRow[];
 total: number;
 isLoading: boolean;
 error: string | null;
 industries: { code: string; ko_name: string }[];
 regions: { sido: string; gugun: string }[];

 setFilter: (patch: Partial<ListingFilter>) => void;
 setPage: (page: number) => void;
 fetchListings: () => Promise<void>;
 fetchMeta: () => Promise<void>;
}

export const useMarketStore = create<MarketState>((set, get) => ({
 filter: { side: 'seeking_advertiser' },
 page: 0,
 limit: 30,
 rows: [],
 total: 0,
 isLoading: false,
 error: null,
 industries: [],
 regions: [],

 setFilter: (patch) => set(state => ({ filter: { ...state.filter, ...patch }, page: 0 })),
 setPage: (page) => set({ page }),

 fetchListings: async () => {
  set({ isLoading: true, error: null });
  try {
   const { filter, page, limit } = get();
   const params = new URLSearchParams();
   params.set('side', filter.side);
   if (filter.industry) params.set('industry', filter.industry);
   if (filter.sido) params.set('sido', filter.sido);
   if (filter.gugun) params.set('gugun', filter.gugun);
   if (typeof filter.priceMin === 'number') params.set('priceMin', String(filter.priceMin));
   if (typeof filter.priceMax === 'number') params.set('priceMax', String(filter.priceMax));
   if (filter.q && filter.q.trim()) params.set('q', filter.q.trim());
   params.set('limit', String(limit));
   params.set('offset', String(page * limit));

   const res = await fetch(`/market/listings?${params}`, { credentials: 'include' });
   const json = await res.json();
   if (!res.ok || !json.success) throw new Error(json.error || '검색 실패');
   set({ rows: json.data.rows, total: json.data.total });
  } catch (err: any) {
   set({ error: err.message || '검색 실패' });
  } finally {
   set({ isLoading: false });
  }
 },

 fetchMeta: async () => {
  try {
   const [iRes, rRes] = await Promise.all([
    fetch('/market/listings/meta/industries'),
    fetch('/market/listings/meta/regions'),
   ]);
   const iJson = await iRes.json();
   const rJson = await rRes.json();
   set({
    industries: iJson?.data ?? [],
    regions: rJson?.data ?? [],
   });
  } catch {
   /* ignore — 메타 없어도 페이지는 동작 */
  }
 },
}));
